import React, { useState } from 'react'
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    Tab,
    Tabs,
    Typography,
} from '@material-ui/core'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { adminWorkWithTenant } from 'components/api'
import { useAuthentication } from 'components/AuthenticationContext'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'
import { ErrorPop } from 'components/ErrorPop'
import { TenantProvider, useTenant } from '../context/TenantContext'
import { TenantOverviewTab } from './TenantOverviewTab'
import { TenantClientAppsTab } from './TenantClientAppsTab'
import { TenantEmailsTab } from './TenantEmailsTab'
import { TenantSurveysTab } from './TenantSurveysTab'
import { TenantSettingsTab } from './TenantSettingsTab'
import { TenantTrustMetricsTab } from './TenantTrustMetricsTab'

const TABS = [
    { label: 'Overview', path: '' },
    { label: 'Client App', path: 'client-apps' },
    { label: 'Emails', path: 'emails' },
    { label: 'Surveys', path: 'surveys' },
    { label: 'Trust Metrics', path: 'trust-metrics' },
    { label: 'Settings', path: 'settings' },
]

function tabIndexFromPath(tabPath) {
    if (!tabPath) return 0
    const idx = TABS.findIndex(t => t.path === tabPath)
    return idx >= 0 ? idx : 0
}

function getTabFromPath(pathname, tenantId) {
    const prefix = `/superadmin/tenants/${tenantId}`
    if (pathname === prefix || pathname === `${prefix}/`) return ''
    return pathname.replace(`${prefix}/`, '')
}


function TenantDetailContent() {
    const { tenantId, tenantName, logo } = useTenant()
    const authState = useAuthentication()
    const currentTenantId = authState.attributes['custom:tenantid']
    const history = useHistory()
    const location = useLocation()
    const tab = getTabFromPath(location.pathname, tenantId)
    const [showConfirm, setShowConfirm] = useState(false)
    const [switchError, setSwitchError] = useState(null)

    const isCurrent = currentTenantId === tenantId
    const basePath = `/superadmin/tenants/${tenantId}`
    const activeTab = tabIndexFromPath(tab)

    const handleTabChange = (_, newIndex) => {
        const target = TABS[newIndex]
        const path = target.path ? `${basePath}/${target.path}` : basePath
        history.push(path)
    }

    const workWithTenant = () => {
        adminWorkWithTenant(tenantId, authState.username)
            .then(() => {
                authState.signOut()
                window.location = '/'
            })
            .catch(err => {
                setSwitchError(getApiErrorMessage(err, 'Failed to switch tenant'))
                setShowConfirm(false)
            })
    }

    return (
        <>
            <div className="superadmin-tenant-header">
                <img src={logo} alt={tenantName} />
                <div style={{ flex: 1 }}>
                    <Typography variant="h6">{tenantName}</Typography>
                    <Typography variant="caption" color="textSecondary">
                        Tenant ID: {tenantId}
                    </Typography>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {isCurrent && <Chip label="Current" color="secondary" size="small" />}
                    {!isCurrent && (
                        <Button variant="outlined" size="small" onClick={() => setShowConfirm(true)}>
                            Use Tenant
                        </Button>
                    )}
                </div>
            </div>

            <div className="superadmin-tenant-tabs">
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    {TABS.map(t => (
                        <Tab key={t.label} label={t.label} />
                    ))}
                </Tabs>
            </div>

            {tab === 'client-apps' && <TenantClientAppsTab />}
            {tab === 'emails' && <TenantEmailsTab />}
            {tab === 'surveys' && <TenantSurveysTab />}
            {tab === 'trust-metrics' && <TenantTrustMetricsTab />}
            {tab === 'settings' && <TenantSettingsTab />}
            {(!tab || !TABS.some(t => t.path === tab)) && <TenantOverviewTab />}

            <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
                <DialogContent>
                    <DialogContentText>
                        <Typography variant="h6">Use tenant {tenantName}?</Typography>
                        <Typography color="secondary">
                            Warning: You will be logged out so the new tenant claim can take effect.
                        </Typography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowConfirm(false)} color="secondary">Cancel</Button>
                    <Button variant="contained" color="primary" onClick={workWithTenant}>Use</Button>
                </DialogActions>
            </Dialog>
            <ErrorPop error={switchError} setError={setSwitchError} timeout={6000} />
        </>
    )
}

export function TenantDetailSection() {
    const { tenantId } = useParams()

    return (
        <TenantProvider tenantId={tenantId}>
            <TenantDetailContent />
        </TenantProvider>
    )
}
